import React, { useState, useEffect } from 'react';
import { Play, Clock, Users, AlertTriangle, UserCog } from 'lucide-react';
import { colors } from '../../constants/theme';
import { matchesService, playersService } from '../../services/api';
import { phaseLabel } from '../../constants/phases';
import { CATEGORIES, CATEGORY_LABELS } from '../../constants/categories';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';

const ALL_CATEGORIES = 'todas';

const MatchScheduler = ({ onMatchesUpdate }) => {
  const [readyMatches, setReadyMatches] = useState([]);
  const [currentMatches, setCurrentMatches] = useState([]);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [swappingMatchId, setSwappingMatchId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Carregando dados do agendador...');

      const [readyResponse, currentResponse] = await Promise.all([
        matchesService.getReady().catch(() => ({ success: false, data: [] })),
        matchesService.getCurrent().catch(() => ({ success: false, data: [] }))
      ]);

      console.log('⚽ Ready matches:', readyResponse);
      console.log('🏃 Current matches:', currentResponse);

      if (readyResponse.success) {
        setReadyMatches(readyResponse.data || []);
      }

      if (currentResponse.success) {
        setCurrentMatches(currentResponse.data || []);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setError('Erro ao carregar partidas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMatch = (matchId) => {
    setSelectedMatches(prev => {
      const isSelected = prev.includes(matchId);
      const newSelection = isSelected 
        ? prev.filter(id => id !== matchId)
        : [...prev, matchId];
      
      // Máximo 2 partidas
      if (newSelection.length > 2) {
        return prev;
      }
      
      return newSelection;
    });
  };

  const handleStartMatches = async () => {
    if (selectedMatches.length === 0) {
      alert('Selecione pelo menos uma partida para iniciar');
      return;
    }

    if (currentMatches.length + selectedMatches.length > 2) {
      alert(`Já existem ${currentMatches.length} partidas em andamento. Máximo permitido: 2`);
      return;
    }

    setActionLoading(true);
    try {
      console.log(`🚀 Iniciando ${selectedMatches.length} partida(s):`, selectedMatches);

      if (selectedMatches.length === 1) {
        // Iniciar uma partida
        const response = await matchesService.startMatch(selectedMatches[0]);
        if (response.success) {
          alert('Partida iniciada com sucesso!');
        }
      } else {
        // Iniciar múltiplas partidas
        const response = await matchesService.startMultipleMatches(selectedMatches);
        if (response.success) {
          alert(`${response.data.length} partida(s) iniciada(s) com sucesso!`);
        }
      }

      setSelectedMatches([]);
      await loadData();
      onMatchesUpdate && onMatchesUpdate();
    } catch (error) {
      console.error('❌ Erro ao iniciar partidas:', error);
      alert('Erro ao iniciar partida(s): ' + (error.error?.message || 'Erro desconhecido'));
    } finally {
      setActionLoading(false);
    }
  };

  const canStartMore = currentMatches.length < 2;
  const maxSelectable = Math.min(2 - currentMatches.length, 2);

  const matchCategory = (match) => match.category || match.player1?.category;

  // Só as categorias que realmente têm partida esperando viram aba.
  const categoriesWithMatches = CATEGORIES.filter((cat) =>
    readyMatches.some((m) => matchCategory(m) === cat),
  );

  const visibleMatches =
    categoryFilter === ALL_CATEGORIES
      ? readyMatches
      : readyMatches.filter((m) => matchCategory(m) === categoryFilter);

  // Seleção sobrevive à troca de aba (dá pra iniciar infantil + juvenil juntos),
  // então avisa quando há partida selecionada fora do filtro atual.
  const hiddenSelectedCount = selectedMatches.filter(
    (id) => !visibleMatches.some((m) => m.id === id),
  ).length;

  if (loading) {
    return <LoadingSpinner size="medium" text="Carregando partidas..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={loadData} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold" style={{ color: colors.primary }}>
          Iniciar Partidas
        </h2>
        <button
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
        >
          Atualizar
        </button>
      </div>

      {/* Status atual */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Play className="h-8 w-8 text-green-500 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Em Andamento</p>
              <p className="text-2xl font-bold">{currentMatches.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-blue-500 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Prontas</p>
              <p className="text-2xl font-bold">{readyMatches.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-orange-500 mr-3" />
            <div>
              <p className="text-sm text-gray-600">Selecionadas</p>
              <p className="text-2xl font-bold">{selectedMatches.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Aviso sobre limite */}
      {!canStartMore && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
            <p className="text-yellow-800">
              Máximo de 2 partidas simultâneas já atingido. Finalize uma partida antes de iniciar outra.
            </p>
          </div>
        </div>
      )}

      {/* Partidas prontas.
          A lista aparece mesmo com o limite de simultâneas atingido: trocar
          quem joga uma partida que ainda não começou independe de poder
          iniciar outra agora. Só os controles de início ficam escondidos. */}
      {readyMatches.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4 gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-800">
              Partidas Prontas ({readyMatches.length})
            </h3>
            {canStartMore && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedMatches([])}
                  disabled={selectedMatches.length === 0}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm disabled:opacity-50"
                >
                  Limpar Seleção
                </button>
                <button
                  onClick={handleStartMatches}
                  disabled={selectedMatches.length === 0 || actionLoading}
                  className="px-4 py-2 text-white rounded font-medium hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: colors.primary }}
                >
                  {actionLoading ? 'Iniciando...' : `Iniciar ${selectedMatches.length} Partida(s)`}
                </button>
              </div>
            )}
          </div>

          {/* Filtro por categoria */}
          <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b">
            <button
              onClick={() => setCategoryFilter(ALL_CATEGORIES)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                categoryFilter === ALL_CATEGORIES
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={{
                backgroundColor:
                  categoryFilter === ALL_CATEGORIES ? colors.primary : undefined,
              }}
            >
              Todas ({readyMatches.length})
            </button>

            {categoriesWithMatches.map((cat) => {
              const count = readyMatches.filter((m) => matchCategory(m) === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                    categoryFilter === cat
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={{
                    backgroundColor: categoryFilter === cat ? colors.primary : undefined,
                  }}
                >
                  {CATEGORY_LABELS[cat] || cat} ({count})
                </button>
              );
            })}
          </div>

          <p className="text-sm text-gray-600 mb-4">
            {canStartMore
              ? `Você pode selecionar até ${maxSelectable} partida(s) para iniciar simultaneamente.`
              : 'Limite de partidas simultâneas atingido — finalize uma para poder iniciar outra. Você ainda pode trocar os atletas das partidas abaixo.'}
          </p>

          {hiddenSelectedCount > 0 && (
            <div className="flex items-start gap-2 p-3 mb-4 bg-yellow-50 border border-yellow-200 rounded">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-yellow-800">
                {hiddenSelectedCount} partida(s) selecionada(s) em outra categoria também
                serão iniciadas. Use "Limpar Seleção" se não for isso que você quer.
              </p>
            </div>
          )}

          {visibleMatches.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-gray-500 text-sm">
                Nenhuma partida pronta nesta categoria.
              </p>
              <button
                onClick={() => setCategoryFilter(ALL_CATEGORIES)}
                className="mt-2 text-sm underline"
                style={{ color: colors.primary }}
              >
                Ver todas
              </button>
            </div>
          ) : (
          <div className="divide-y divide-gray-200">
            {visibleMatches.map((match) => (
              <div key={match.id} className="py-4 first:pt-0 last:pb-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <input
                      type="checkbox"
                      checked={selectedMatches.includes(match.id)}
                      onChange={() => handleSelectMatch(match.id)}
                      disabled={
                        !canStartMore ||
                        (!selectedMatches.includes(match.id) &&
                          selectedMatches.length >= maxSelectable)
                      }
                      title={
                        canStartMore
                          ? 'Selecionar para iniciar'
                          : 'Limite de partidas simultâneas atingido'
                      }
                      className="w-4 h-4 rounded border-gray-300 focus:ring-2 disabled:opacity-40"
                      style={{ focusRingColor: colors.primary }}
                    />

                    <div className="flex items-center space-x-6">
                      <div className="text-center">
                        <p className="font-medium">{match.player1?.name || 'Jogador 1'}</p>
                        <p className="text-xs text-gray-500">{match.player1?.category || 'N/A'}</p>
                      </div>

                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-500">VS</p>
                        <p className="text-xs text-gray-400">{phaseLabel(match.phase)}</p>
                      </div>

                      <div className="text-center">
                        <p className="font-medium">{match.player2?.name || 'Jogador 2'}</p>
                        <p className="text-xs text-gray-500">{match.player2?.category || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setSwappingMatchId(swappingMatchId === match.id ? null : match.id)
                      }
                      title="Trocar quem joga esta partida"
                      className="flex items-center gap-1 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                    >
                      <UserCog className="h-3.5 w-3.5" />
                      Trocar atleta
                    </button>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {phaseLabel(match.phase).toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">
                      ID: {match.id}
                    </span>
                  </div>
                </div>

                {swappingMatchId === match.id && (
                  <PlayerSwapForm
                    match={match}
                    onCancel={() => setSwappingMatchId(null)}
                    onSaved={async () => {
                      setSwappingMatchId(null);
                      await loadData();
                      onMatchesUpdate && onMatchesUpdate();
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Partidas em andamento */}
      {currentMatches.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b" style={{ backgroundColor: colors.primary }}>
            <h3 className="text-white font-semibold">
              Partidas em Andamento ({currentMatches.length})
            </h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {currentMatches.map((match) => (
              <div key={match.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <p className="font-medium">{match.player1?.name || 'Jogador 1'}</p>
                      <p className="text-xs text-gray-500">{match.player1?.category || 'N/A'}</p>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                        {match.player1?.goals || 0}
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-lg font-bold text-gray-500">VS</p>
                      <p className="text-xs text-gray-400">{match.phase}</p>
                    </div>
                    
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                        {match.player2?.goals || 0}
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <p className="font-medium">{match.player2?.name || 'Jogador 2'}</p>
                      <p className="text-xs text-gray-500">{match.player2?.category || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                      EM ANDAMENTO
                    </span>
                    {match.startTime && (
                      <span className="text-xs text-gray-500">
                        {new Date(match.startTime).toLocaleTimeString('pt-BR')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {readyMatches.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-2">Nenhuma partida pronta para iniciar</p>
          <p className="text-sm text-gray-400">
            As partidas aparecerão aqui quando estiverem prontas para começar
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Troca quem joga uma partida que ainda não começou.
 *
 * Serve ao desempate por pênaltis da repescagem: quando os melhores 3ºs
 * empatam em tudo, o sistema monta a chave com um deles e o organizador
 * coloca aqui quem realmente passou, sem gerar partida nova.
 */
function PlayerSwapForm({ match, onCancel, onSaved }) {
  const category = match.category || match.player1?.category;
  const currentPlayer1Id = match.player1Id ?? match.player1?.id;
  const currentPlayer2Id = match.player2Id ?? match.player2?.id;

  const [candidates, setCandidates] = useState([]);
  const [player1Id, setPlayer1Id] = useState(currentPlayer1Id);
  const [player2Id, setPlayer2Id] = useState(currentPlayer2Id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await playersService.getAll({ category, limit: 100 });
        if (!active) return;
        if (resp.success) setCandidates(resp.data?.players || []);
      } catch (e) {
        if (active) setError('Não foi possível carregar os atletas desta categoria');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [category]);

  const changed = player1Id !== currentPlayer1Id || player2Id !== currentPlayer2Id;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const resp = await matchesService.replacePlayers(match.id, {
        player1Id: player1Id !== currentPlayer1Id ? Number(player1Id) : undefined,
        player2Id: player2Id !== currentPlayer2Id ? Number(player2Id) : undefined,
      });
      if (resp.success) await onSaved();
    } catch (e) {
      setError(e.error?.message || 'Erro ao trocar atletas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 p-4 bg-gray-50 border rounded-lg">
      <p className="text-sm text-gray-700 mb-3">
        Trocar quem joga a partida <strong>{match.id}</strong> (
        {phaseLabel(match.phase)} · {category}). Use isto depois da disputa de
        pênaltis para colocar quem realmente passou.
      </p>

      {error && (
        <div className="flex items-start gap-2 p-2 mb-3 bg-red-50 border border-red-200 rounded">
          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Carregando atletas...</p>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Atleta 1
              </label>
              <select
                value={player1Id}
                onChange={(e) => setPlayer1Id(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                {candidates.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Atleta 2
              </label>
              <select
                value={player2Id}
                onChange={(e) => setPlayer2Id(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                {candidates.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!changed || saving}
              className="px-4 py-2 text-white rounded text-sm hover:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: colors.primary }}
            >
              {saving ? 'Salvando...' : 'Salvar troca'}
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm"
            >
              Cancelar
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default MatchScheduler;